import { type ReactNode, useEffect, useId, useState } from 'react';
import {
  Button,
  Checkbox,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { ChevronDown, ChevronUp, ListFilter, Trash2 } from 'lucide-react';

import {
  type TaskFilterFamily,
  type TaskFilterVisitDiscipline,
  type TaskFilterVisitKind,
  type TaskTypeFilterValue,
  type TherapyDiscipline,
  createDefaultTaskTypeFilter,
  getTaskTypeFilterSummary,
  isTaskTypeFilterDefault,
  normalizeTaskTypeFilter,
  setTaskTypeTopLevel,
  setTaskTypeVisitDiscipline,
  setTaskTypeVisitSubtype,
  toggleTaskTypeTherapy,
} from './taskTypeFilters';

interface TaskTypeFilterProps {
  value: TaskTypeFilterValue;
  onChange: (value: TaskTypeFilterValue) => void;
  variant?: 'compact' | 'inline';
  totalCount?: number;
  filteredCount?: number;
  className?: string;
}

const THERAPY_DISCIPLINES: TherapyDiscipline[] = ['PT', 'OT', 'ST'];
const THERAPY_DISCIPLINE_TOOLTIPS: Record<TherapyDiscipline, string> = {
  PT: 'Show only physical therapy visits',
  OT: 'Show only occupational therapy visits',
  ST: 'Show only speech therapy visits',
};

interface TaskTypeFilterBodyProps {
  filter: TaskTypeFilterValue;
  variant: 'compact' | 'inline';
  checkboxIdPrefix: string;
  onChange: (value: TaskTypeFilterValue) => void;
}

interface TaskFilterButtonProps {
  label: string;
  tooltip: string;
  pressed: boolean;
  onClick: () => void;
}

const TaskFilterButton = ({
  label,
  tooltip,
  pressed,
  onClick,
}: TaskFilterButtonProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        type='button'
        size='sm'
        variant={pressed ? 'default' : 'outline'}
        onClick={onClick}
        aria-pressed={pressed}
        title={tooltip}
      >
        {label}
      </Button>
    </TooltipTrigger>
    <TooltipContent>{tooltip}</TooltipContent>
  </Tooltip>
);

interface TaskFilterClearButtonProps {
  onClick: () => void;
}

const TaskFilterClearButton = ({ onClick }: TaskFilterClearButtonProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        type='button'
        size='sm'
        variant='outline'
        onClick={onClick}
        title='Clear active task type filters'
      >
        <Trash2 className='h-4 w-4' aria-hidden='true' />
        Clear
      </Button>
    </TooltipTrigger>
    <TooltipContent>Clear active task type filters</TooltipContent>
  </Tooltip>
);

interface TaskTypeFilterHeaderProps {
  metadata: string;
  actions?: ReactNode;
  testId?: string;
}

const TaskTypeFilterHeader = ({
  metadata,
  actions,
  testId,
}: TaskTypeFilterHeaderProps) => (
  <div
    data-testid={testId}
    className='flex w-full items-start justify-between gap-3'
  >
    <div className='min-w-0 flex items-center gap-2'>
      <ListFilter
        className='h-4 w-4 shrink-0 text-muted-foreground'
        aria-hidden='true'
      />
      <div className='min-w-0 flex flex-col'>
        <span className='text-sm font-medium'>Task filters</span>
        <span className='truncate text-xs text-muted-foreground'>
          {metadata}
        </span>
      </div>
    </div>
    {actions ? <div className='shrink-0'>{actions}</div> : null}
  </div>
);

interface TaskFilterToggleButtonProps {
  expanded: boolean;
  onClick: () => void;
}

const TaskFilterToggleButton = ({
  expanded,
  onClick,
}: TaskFilterToggleButtonProps) => {
  const label = expanded ? 'Hide' : 'Show';
  const tooltip = `${label} task type filters`;
  const Icon = expanded ? ChevronUp : ChevronDown;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type='button'
          size='sm'
          variant='ghost'
          onClick={onClick}
          aria-expanded={expanded}
          title={tooltip}
        >
          <Icon className='h-4 w-4' aria-hidden='true' />
          {label}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
};

const TaskTypeFilterBody = ({
  filter,
  variant,
  checkboxIdPrefix,
  onChange,
}: TaskTypeFilterBodyProps) => {
  const setTopLevel = (family: TaskFilterFamily) => {
    onChange(setTaskTypeTopLevel(filter, family));
  };

  const setVisitCategory = (category: TaskFilterVisitKind) => {
    onChange(
      setTaskTypeVisitSubtype(
        filter,
        filter.visitKind === category ? 'all' : category
      )
    );
  };

  const setVisitDiscipline = (discipline: TaskFilterVisitDiscipline) => {
    onChange(
      setTaskTypeVisitDiscipline(
        filter,
        filter.discipline === discipline ? 'all' : discipline
      )
    );
  };

  const toggleTherapy = (therapy: TherapyDiscipline) => {
    onChange(toggleTaskTypeTherapy(filter, therapy));
  };

  const sectionClass =
    variant === 'inline'
      ? 'w-full flex flex-wrap items-center gap-2'
      : 'flex flex-wrap items-center gap-2';

  return (
    <div className={sectionClass}>
      <TaskFilterButton
        label='All'
        tooltip='Show all task types'
        pressed={filter.family === 'all'}
        onClick={() => setTopLevel('all')}
      />
      <TaskFilterButton
        label='Order'
        tooltip='Show order tasks only'
        pressed={filter.family === 'order'}
        onClick={() => setTopLevel('order')}
      />
      <TaskFilterButton
        label='Visit'
        tooltip='Show visit tasks only'
        pressed={filter.family === 'visit'}
        onClick={() => setTopLevel('visit')}
      />

      {filter.family === 'visit' && (
        <>
          <Separator orientation='vertical' className='h-6' />
          <TaskFilterButton
            label='OASIS'
            tooltip='Show only OASIS visits'
            pressed={filter.visitKind === 'oasis'}
            onClick={() => setVisitCategory('oasis')}
          />
          <TaskFilterButton
            label='Non-OASIS'
            tooltip='Show only non-OASIS visits'
            pressed={filter.visitKind === 'non_oasis'}
            onClick={() => setVisitCategory('non_oasis')}
          />
          <TaskFilterButton
            label='SN'
            tooltip='Show only skilled nursing visits'
            pressed={filter.discipline === 'sn'}
            onClick={() => setVisitDiscipline('sn')}
          />
          <TaskFilterButton
            label='Therapy'
            tooltip='Show only therapy visits'
            pressed={filter.discipline === 'therapy'}
            onClick={() => setVisitDiscipline('therapy')}
          />

          {filter.discipline === 'therapy' && (
            <div className='flex items-center gap-2 pl-2 pt-2 border-t border-border w-full'>
              <span className='text-xs text-muted-foreground'>
                Disciplines:
              </span>
              {THERAPY_DISCIPLINES.map(therapy => {
                const id = `${checkboxIdPrefix}-${therapy.toLowerCase()}`;
                const tooltip = THERAPY_DISCIPLINE_TOOLTIPS[therapy];
                return (
                  <Tooltip key={therapy}>
                    <TooltipTrigger asChild>
                      <span
                        className='inline-flex items-center gap-2 text-sm'
                        title={tooltip}
                      >
                        <Checkbox
                          id={id}
                          checked={filter.therapyDisciplines.includes(therapy)}
                          onCheckedChange={() => toggleTherapy(therapy)}
                          checkboxSize='sm'
                        />
                        <label htmlFor={id}>{therapy}</label>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{tooltip}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const TaskTypeFilter = ({
  value,
  onChange,
  variant = 'compact',
  totalCount,
  filteredCount,
  className,
}: TaskTypeFilterProps) => {
  const checkboxIdPrefix = useId();
  const filter = normalizeTaskTypeFilter(value);
  const summary = getTaskTypeFilterSummary(filter);
  const isDefault = isTaskTypeFilterDefault(filter);
  const [filtersVisible, setFiltersVisible] = useState(true);

  useEffect(() => {
    if (isDefault) {
      setFiltersVisible(true);
    }
  }, [isDefault]);

  const clearFilter = () => {
    onChange(createDefaultTaskTypeFilter());
  };
  const clearAction = isDefault ? null : (
    <TaskFilterClearButton onClick={clearFilter} />
  );
  const toggleAction = (
    <TaskFilterToggleButton
      expanded={filtersVisible}
      onClick={() => setFiltersVisible(current => !current)}
    />
  );
  const headerActions = (
    <div className='flex items-center gap-1'>
      {clearAction}
      {toggleAction}
    </div>
  );

  const filterBody = (
    <TaskTypeFilterBody
      filter={filter}
      variant={variant}
      checkboxIdPrefix={checkboxIdPrefix}
      onChange={onChange}
    />
  );

  if (variant === 'compact') {
    return (
      <TooltipProvider delayDuration={0}>
        <div
          className={
            'space-y-3 border-b border-border bg-muted/20 p-3' +
            (className ? ` ${className}` : '')
          }
        >
          <TaskTypeFilterHeader
            metadata={summary}
            actions={headerActions}
            testId='task-filter-compact-header'
          />
          {filtersVisible ? filterBody : null}
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={
          'w-full flex flex-col items-start gap-3 px-3 pt-3 text-left' +
          (className ? ` ${className}` : '')
        }
      >
        <TaskTypeFilterHeader
          metadata={
            typeof filteredCount === 'number' && typeof totalCount === 'number'
              ? `Showing ${filteredCount} of ${totalCount} rows`
              : 'Showing results by task type'
          }
          actions={headerActions}
          testId='task-filter-inline-header'
        />
        {filtersVisible ? (
          <div className='w-full pb-2'>{filterBody}</div>
        ) : null}
      </div>
    </TooltipProvider>
  );
};

export default TaskTypeFilter;
